// Side-effect import: forces app-shell's `Window.analytics` augmentation
// (`SegmentAnalytics.AnalyticsJS`) to load before tracking-plan's
// `clients/js/segment.ts` augmentation (`AnalyticsSnippet`). Without this,
// the order in which the conflicting `Window.analytics` declarations get
// merged in publish-frontend's typecheck collapses the intersection to
// `never`, breaking app-shell's `trackUser.ts`. env.ts is one of the first
// modules loaded by publish-frontend, so the side effect anchors here.
import '@buffer-mono/app-shell'

import {
  getBibeInfo,
  getEnvFromHostname,
  getUrl,
} from '@buffer-mono/shared-utils/bibe'

import { getDebugLogger } from '~publish/legacy/utils/getDebugLogger'

const debug = getDebugLogger('env')

export const IS_WITH_PROD = import.meta.env.MODE === 'dev-with-prod'
export const IS_WITH_STAGING = import.meta.env.MODE === 'dev-with-staging'
export const IS_WITH_PROD_BIBE = import.meta.env.MODE === 'dev-with-prod-bibe'
export const IS_WITH_STAGING_BIBE =
  import.meta.env.MODE === 'dev-with-staging-bibe'
export const BIBE_NUMBER = import.meta.env.VITE_BIBE_NUMBER

const isEmbedded = window.location.pathname === '/embedded-compose'

type EnvName = 'production' | 'staging' | 'development' | 'test'

export type Env = {
  VITE_NODE_ENV: EnvName
  VITE_COMMIT_HASH: string
  VITE_SPLIT_ENV: string
  VITE_STRIPE_PUBLISHABLE_KEY: string
  VITE_SEGMENT_KEY: string
  VITE_PUBLIC_SEGMENT_KEY: string
  VITE_GRAPHQL_API: string
  VITE_GRAPHQL_MOCK_API: string
  VITE_CALLBACK_URL: string
  VITE_UPLOADCARE_KEY: string
  VITE_SC_ATTR: string
  VITE_UNSPLASH_APP_ID: string
  VITE_LINKEDIN_APP_ID: string
  VITE_X_BUFFER_CLIENT_ID: string
  VITE_FULLSTORY_ORG_ID: string
  VITE_CUSTOMERIO_SITE_ID: string
  VITE_CUSTOMERIO_API_KEY: string
  VITE_MCP_SERVER_URL: string
  VITE_UPLOADS_API: string
  VITE_BRAINFISH_WIDGET_KEY: string
}

const COMMON_VARS: Pick<
  Env,
  | 'VITE_COMMIT_HASH'
  | 'VITE_SC_ATTR'
  | 'VITE_X_BUFFER_CLIENT_ID'
  | 'VITE_UPLOADCARE_KEY'
  | 'VITE_GRAPHQL_API'
  | 'VITE_GRAPHQL_MOCK_API'
  | 'VITE_MCP_SERVER_URL'
  | 'VITE_UPLOADS_API'
  | 'VITE_PUBLIC_SEGMENT_KEY'
> = {
  // Production BIBEs use production APIs but send public analytics to Dev.
  VITE_PUBLIC_SEGMENT_KEY:
    window.location.hostname === 'publish.buffer.com' && import.meta.env.PROD
      ? 'f0z0RFg4jEgHakjRHneduHDZDJWvM3LD'
      : 'UBerbPLZqMQ4LbLbQfGkeLdhvsxrRl1A',
  VITE_COMMIT_HASH: `${import.meta.env.VITE_COMMIT_HASH}`,
  VITE_SC_ATTR: 'publish-styles',
  VITE_X_BUFFER_CLIENT_ID: 'webapp-publishing',
  VITE_UPLOADCARE_KEY: '12767249c4399a123c3b',
  // /__graphql is a same-origin proxy for the extension sidebar iframe (see
  // docker/nginx-config/publish-frontend/production.conf), which derives
  // the matching per-environment graph host from the request's own Host
  // header and fails closed on anything it doesn't recognize — safe to use
  // unconditionally here rather than gating by environment on this side too.
  VITE_GRAPHQL_API: isEmbedded
    ? '/__graphql'
    : getUrl('https://graph.buffer.com'),
  VITE_GRAPHQL_MOCK_API: 'https://mock-graph.dev.buffer.com/',
  VITE_MCP_SERVER_URL: getUrl('https://mcp.buffer.com/mcp'),
  VITE_UPLOADS_API: getUrl('https://uploads.buffer.com/uploads'),
}

const PRODUCTION_VARS: Env = {
  ...COMMON_VARS,
  VITE_NODE_ENV: 'production',
  VITE_SPLIT_ENV: 'production',
  VITE_STRIPE_PUBLISHABLE_KEY: 'pk_qOmHaWApLCX5OoeWKQ3NrArhoyWEi',
  VITE_SEGMENT_KEY: '9Plsiyvw9NEgXEN7eSBwiAGlHD3DHp0A',
  VITE_CALLBACK_URL: 'https://callbacks.buffer.com',
  VITE_UNSPLASH_APP_ID: '381197',
  VITE_LINKEDIN_APP_ID: '208723654',
  // 9F6GW is the main FullStory account for Buffer
  // we use o-1VJQD8-na1 when we run out of sessions
  VITE_FULLSTORY_ORG_ID: '9F6GW',
  VITE_CUSTOMERIO_SITE_ID: 'ccd747b1cffe99847e08',
  VITE_CUSTOMERIO_API_KEY: '61042496ac16f1624aa9',
  VITE_BRAINFISH_WIDGET_KEY:
    'bf_search_widget_bv15qEgl0vkZKmIRySQAAE7Yb7xsVSsaF1qPbd',
}

const STAGING_VARS: Env = {
  ...COMMON_VARS,
  VITE_NODE_ENV: 'staging',
  VITE_SPLIT_ENV: 'staging',
  VITE_STRIPE_PUBLISHABLE_KEY: 'pk_dGKqAIFsUQonSYGPBM9Rek71IHOcL',
  VITE_SEGMENT_KEY: 'qsP2UfgODyoJB3px9SDkGX5I6wDtdQ6a',
  VITE_CALLBACK_URL: 'https://callbacks.dev.buffer.com',
  VITE_UNSPLASH_APP_ID: '380646',
  VITE_LINKEDIN_APP_ID: '208723654',
  VITE_FULLSTORY_ORG_ID: 'mock',
  VITE_CUSTOMERIO_SITE_ID: '7c16d71a254f8ac07907',
  VITE_CUSTOMERIO_API_KEY: '9990a619b68d4cf134a1',
  VITE_BRAINFISH_WIDGET_KEY: 'bf_agent_aePm8mKc93vJDgWXCNVSvxnAKp3hl5DpXBcZjc',
}

const DEVELOPMENT_VARS: Env = {
  ...STAGING_VARS,
  VITE_NODE_ENV: 'development',
  VITE_SPLIT_ENV: 'local',
  VITE_GRAPHQL_MOCK_API: 'https://graph.local.buffer.com',
  VITE_CALLBACK_URL: 'https://callbacks.local.buffer.com',
  VITE_CUSTOMERIO_SITE_ID: '82d38857f6dae390778e',
  VITE_CUSTOMERIO_API_KEY: '63abb1784168503cb485',
}

function selectEnv(): Env {
  const envName = getEnvFromHostname()

  if (IS_WITH_PROD) {
    // overrides for rushx build:watch:withProd
    // COMMON_VARS's getUrl() calls already resolve to these same URLs via
    // the mode-driven override in @buffer-mono/shared-utils/bibe — pinned
    // here as explicit literals so this override doesn't depend on that
    // global-mode side channel staying wired correctly.
    return {
      ...PRODUCTION_VARS,
      VITE_NODE_ENV: 'development',
      VITE_GRAPHQL_API: 'https://graph.buffer.com',
      VITE_MCP_SERVER_URL: 'https://mcp.buffer.com/mcp',
      VITE_UPLOADS_API: 'https://uploads.buffer.com/uploads',
    }
  }

  if (IS_WITH_STAGING) {
    // overrides for rushx build:watch:withStaging
    // Same reasoning as IS_WITH_PROD.
    return {
      ...STAGING_VARS,
      VITE_NODE_ENV: 'development',
      VITE_GRAPHQL_API: 'https://graph.dev.buffer.com',
      VITE_MCP_SERVER_URL: 'https://mcp.dev.buffer.com/mcp',
      VITE_UPLOADS_API: 'https://uploads.dev.buffer.com/uploads',
    }
  }

  if (IS_WITH_PROD_BIBE) {
    // overrides for rushx build:watch:withProd:withBibe <PR number>
    // Same reasoning as IS_WITH_PROD, targeting the numbered BIBE instead of
    // the fixed production hostname. tools/vite-config's getBaseViteConfig
    // throws before this ever runs if VITE_BIBE_NUMBER isn't a valid number.
    const bibeHost = `${BIBE_NUMBER}.prod-bibes`
    return {
      ...PRODUCTION_VARS,
      VITE_NODE_ENV: 'development',
      VITE_GRAPHQL_API: `https://${bibeHost}.graph.buffer.com`,
      VITE_MCP_SERVER_URL: `https://${bibeHost}.mcp.buffer.com/mcp`,
      VITE_UPLOADS_API: `https://${bibeHost}.uploads.buffer.com/uploads`,
    }
  }

  if (IS_WITH_STAGING_BIBE) {
    // overrides for rushx build:watch:withStaging:withBibe <PR number>
    // Same reasoning as IS_WITH_STAGING, targeting the numbered BIBE instead
    // of the fixed staging hostname. tools/vite-config's getBaseViteConfig
    // throws before this ever runs if VITE_BIBE_NUMBER isn't a valid number.
    const bibeHost = `${BIBE_NUMBER}.staging-bibes`
    return {
      ...STAGING_VARS,
      VITE_NODE_ENV: 'development',
      VITE_GRAPHQL_API: `https://${bibeHost}.graph.dev.buffer.com`,
      VITE_MCP_SERVER_URL: `https://${bibeHost}.mcp.dev.buffer.com/mcp`,
      VITE_UPLOADS_API: `https://${bibeHost}.uploads.dev.buffer.com/uploads`,
    }
  }

  if (envName === 'development') {
    return DEVELOPMENT_VARS
  }

  if (envName === 'staging') {
    return STAGING_VARS
  }

  return PRODUCTION_VARS
}

export const env = selectEnv()

/**
 * This function checks if we're in a real production environment
 *
 * BIBES is not considered real production
 */
export const isRealProduction = (): boolean => {
  const selectedEnv = selectEnv()
  return selectedEnv.VITE_NODE_ENV === 'production' && getBibeInfo() === null
}

debug('env', {
  env,
  IS_WITH_PROD,
  IS_WITH_STAGING,
  IS_WITH_PROD_BIBE,
  IS_WITH_STAGING_BIBE,
  BIBE_NUMBER,
})
